import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDispatch, useSelector } from '_common/hooks/redux';
import { closeAndResetModal } from '_common/modals/ModalsSlice';
import { createGroup } from 'Groups/redux/GroupsPageSlice';

import { Input, Checkbox, Modal, InputField, Button } from '@ets/design-system';

const MODAL = 'CreateGroupModal';

const CreateGroupModal = () => {
  const intl = useIntl();

  // redux
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const userId = useSelector((state) => state.auth.userId);

  // local
  const [groupName, setGroupName] = useState<string>('');
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [validations, setValidations] = useState<{ name?: string }>({ name: '' });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [isOpen]);

  const handleOnNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGroupName(value);

    // validations
    const validations = { name: '' };
    if (e.target.value.length === 0) {
      validations.name = intl.formatMessage({
        id: 'validation.name.insertName',
      });
    } else if (value.length > 35) {
      validations.name = intl.formatMessage({ id: 'validation.name.longName' }, { length: 35 });
    } else {
      validations.name = '';
    }

    if (Object.keys(validations).length > 0) {
      setValidations(validations);
    }
  };

  const handleOnClickCreate = () => {
    if (groupName && groupName.length > 0) {
      if (isChecked) {
        dispatch(createGroup({ name: groupName, userId }));
      } else {
        dispatch(createGroup({ name: groupName }));
      }
      close();
    } else {
      // TODO: show validation error
    }
  };

  const handleOnClickCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsChecked(!isChecked);
  };

  const close = () => {
    setGroupName('');
    setIsChecked(false);
    setValidations({ name: '' });
    dispatch(closeAndResetModal(MODAL));
  };

  return (
    <Modal width="75rem" open={!!isOpen} close={close} testId="create-group">
      <Modal.Header>
        <FormattedMessage id="groups.createGroup" />
      </Modal.Header>
      <Modal.Body>
        <InputField
          label={intl.formatMessage({ id: 'groups.groupNameTitle' })}
          size="l"
          feedbackContent={validations.name ? validations.name : undefined}
          testId="create-group-name"
        >
          <Input
            ref={inputRef}
            size="l"
            value={groupName}
            placeholder={intl.formatMessage({
              id: 'groups.groupNamePlaceholder',
            })}
            onChange={handleOnNameChange}
            hasError={!!validations.name}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOnClickCreate();
              }
            }}
            testId="create-group-name"
          />
        </InputField>

        <div>
          <Checkbox
            checked={isChecked}
            size="xs"
            onChange={handleOnClickCheck}
            testId="create-group-make-yourself-part-of-group"
          >
            <FormattedMessage id="groups.partOftheGroup" />
          </Checkbox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="create-group-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          onClick={handleOnClickCreate}
          disabled={groupName === '' || !!validations.name}
          testId="create-group-submit"
          style={{ margin: '0 0 0 1rem' }}
        >
          <FormattedMessage id="global.create" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateGroupModal;
